import MultiSelectFilter from '../Filters/MultiSelectFilter/multiSelectFilter';
import MultiPropertiesFilter from '../Filters/MultiPropertiesFilter/multiPropertiesFilter';
import MultiSectionsFilter from '../Filters/MultiSectionsFilter/multiSectionsFilter';
import SearchFilter from '../Filters/SearchFilter/searchFilter';
import DateSelectFilter from '../Filters/DateSelectFilter/dateSelectFilter';
import HorizonFilter from '../Filters/HorizonFilter/horizonFilter';
import { filterTypes } from 'infrastructure/js/enums/filterTypes.js';
import { List } from 'immutable';
import { createLabelHelper } from '../../../utils/labelHelper';
import DateTimeHelper from '../../../utils/dateTimeHelper';
import PermissionManager from '../../../utils/permissionManager';
import {filterAlignmentTypes} from 'infrastructure/js/enums/filterAlignmentTypes';

const createGetDataQuery = function (params, filterConfig = [], pageSize, pageNumber = 0) {

  let query = {
    filters: params != null ? params.filterModel : List(),
    pageSize: pageSize,
    pageNumber: pageNumber,
    sortDirection: '',
    sortByProperty: '',
  };

  if (params != null) {
    if (!params.filterModel || params.filterModel.size === 0) {
      for (let i = 0; i < filterConfig.length; i++) {
        if (filterConfig[i].filterName) {
          query.filters = query.filters.push({
            filterName: filterConfig[i].filterName,
            shouldCreateFilterOptions: !!filterConfig[i].getOptions,
          });
        }
      }
    }
    if (params.sortModel && params.sortModel.filterName) {
      query.sortByProperty = params.sortModel.filterName;
      query.sortDirection = params.sortModel.direction;
    }
  }
  return query;
};

const createGetDataQueryByGridData = function (gridData) {
  if (!gridData) {
    console.error('missing grid data for creating query');
    return null;
  }

  const filterState = gridData.get('filterState');

  // strip irrelevant data from the query
  const filterModel = filterState?.map((filter) => {
    const { extraData, ...filterModel } = filter;
    return filterModel;
  });

  return this.createGetDataQuery(
    {
      filterModel,
      sortModel: gridData.get('sortState'),
    },
    gridData.get('filterConfig')
  );
};

const getFilterValues = function (filters, name) {
  let filter = filters.find((filter) => filter.name === name);

  if (!filter || filter.options.length === 0) {
    return [];
  }

  //return filter.options.map(option => {return [option.value]});
  return filter.options.map((option) => {
    return [option];
  });
};

const addColumnWithMultiSelectFilter = (columnConfig, filtersData) => {
  let filterOptions = {
    filter: MultiSelectFilter,
    filterParams: {
      apply: true,
      filterName: columnConfig.filterName,
      filterWidth: columnConfig.filterWidth,
      filterAlignment: columnConfig.filterAlignment,
      mainPropertyName: columnConfig.fieldName,
      values: getFilterValues(filtersData, columnConfig.filterName),
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithMultiSelectAsyncFilter = (columnConfig, filtersData) => {
  let filterOptions = {
    filter: MultiSelectFilter,
    filterParams: {
      apply: true,
      filterName: columnConfig.filterName,
      filterAlignment: columnConfig.filterAlignment,
      mainPropertyName: columnConfig.fieldName,
      values: getFilterValues(filtersData, columnConfig.filterName),
      newRowsAction: 'keep',
      fetchConfig: columnConfig.fetchConfig,
      asyncMode: true,
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithMultiPropertiesFilter = (columnConfig) => {
  let filterOptions = {
    filter: MultiPropertiesFilter,
    filterParams: {
      filterAlignment: columnConfig.filterAlignment,
      handleSubmit: columnConfig.handleSubmit,
      change: columnConfig.change,
      apply: true,
      properties: columnConfig.propertyTypes,
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithMultiSectionsFilter = (columnConfig) => {
  let filterOptions = {
    filter: MultiSectionsFilter,
    filterParams: {
      filterName: columnConfig.filterName,
      filterWidth: columnConfig.filterWidth,
      filterAlignment: columnConfig.filterAlignment,
      apply: true,
      sections: columnConfig.filterSections,
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithSearchFilter = (columnConfig, filtersData) => {
  let filterOptions = {
    filter: SearchFilter,
    filterParams: {
      handleSubmit: columnConfig.handleSubmit,
      filterName: columnConfig.filterName,
      filterAlignment: columnConfig.filterAlignment,
      values: getFilterValues(filtersData, columnConfig.filterName),
      apply: true,
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithDateSelectFilter = (columnConfig, filtersData) => {
  let filterOptions = {
    filter: DateSelectFilter,
    filterParams: {
      handleSubmit: columnConfig.handleSubmit,
      filterName: columnConfig.filterName,
      filterAlignment: columnConfig.filterAlignment,
      values: getFilterValues(filtersData, columnConfig.filterName),
      apply: true,
      isTime: columnConfig.isTime,
      label: columnConfig.filterLabel,
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };

  return addColumn(columnConfig, filterOptions);
};

const addColumnWithHorizonFilter = (columnConfig, filtersData) => {
  let filterOptions = {
    filter: HorizonFilter,
    filterParams: {
      handleSubmit: columnConfig.handleSubmit,
      filterName: columnConfig.filterName,
      filterAlignment: columnConfig.filterAlignment,
      values: getFilterValues(filtersData, columnConfig.filterName),
      apply: true,
      isTime: columnConfig.isTime,
      label: columnConfig.filterLabel,
      newRowsAction: 'keep',
      ...columnConfig.filterProps,
    },
  };
  return addColumn(columnConfig, filterOptions);
};

const addColumn = (columnConfig, filterOptions = null) => {
  let cellClasses = columnConfig.columnOptions?.cellClass || '';
  switch (columnConfig.columnOptions?.cellEditor) {
    case 'editableMultiCell':
      cellClasses += ' editableMultiCell';
      break;
    case 'editableCell':
      cellClasses += ' editableCell';
      break;
    default:
      break;
  }

  return {
    field: columnConfig.fieldName,
    headerName: columnConfig.title,
    width: columnConfig.width,
    filterWidth: columnConfig.filterWidth,
    filterAlignment: columnConfig.filterAlignment,
    valueFormatter: columnConfig.valueFormatter,
    filterOptions,
    sortingOrder: ['asc', 'desc'],
    menuTabs: filterOptions ? ['filterMenuTab'] : [],
    cellClass: cellClasses,
    ...columnConfig.columnOptions,
  };
};

const createColumnMetaData = (columnData, filtersData) => {
  switch (columnData.filterType) {
    case filterTypes.MULTI_SELECT:
      return addColumnWithMultiSelectFilter(columnData, filtersData);
    case filterTypes.MULTI_SELECT_ASYNC:
      return addColumnWithMultiSelectAsyncFilter(columnData, filtersData);
    case filterTypes.MULTI_PROPERTIES:
      return addColumnWithMultiPropertiesFilter(columnData);
    case filterTypes.MULTI_SECTIONS:
      return addColumnWithMultiSectionsFilter(columnData);
    case filterTypes.SEARCH:
      return addColumnWithSearchFilter(columnData, filtersData);
    case filterTypes.DATE_SELECT:
      return addColumnWithDateSelectFilter(columnData, filtersData);
    case filterTypes.HORIZON:
      return addColumnWithHorizonFilter(columnData, filtersData);
    case filterTypes.NONE:
    default:
      return addColumn(columnData);
  }
};

function applyRowSelectionAsFilter(gridData, query, filterName, valueSelector = null) {
  if (!gridData || !query || !filterName) {
    console.error('missing grid data, query or filterName for application of selection as query.');
    console.error('gridData:', gridData, 'query:', query, 'filterName:', filterName);
    return query;
  }
  if (!valueSelector) {
    valueSelector = (rowData) => {
      return rowData.businessId;
    };
  }
  let values = gridData.get('selectedRowsData')?.toJS()?.map(valueSelector);
  if (!values?.length) return query;

  let filter = query.filters.find((f) => f.filterName === filterName);
  if (filter) filter.values = values;
  else query.filters.push({ filterName, values });

  return query;
}

const getDataQueryForExport = function (params, pageSize, pageNumber = 0) {
  let query = {
    filters: params != null ? params.filterModel : List(),
    pageSize: pageSize,
    pageNumber: pageNumber,
    sortDirection: '',
    sortByProperty: '',
  };

  if (query.filters && query.filters.size > 0) {
    query.filters = query.filters.map((filter) => {
      return {
        filterName: filter.filterName,
        values: filter.values,
        shouldCreateFilterOptions: false,
      };
    });
  }

  if (params != null) {
    if (params.sortModel && params.sortModel.filterName) {
      query.sortByProperty = params.sortModel.filterName;
      query.sortDirection = params.sortModel.direction;
    }
  }
  return query;
};

const getDateFormatHeaderIcon = function () {
  if (!PermissionManager.isDateFormatEnabled()) {
    return {};
  }
  return {
    headerIcon: 'info-icon-blue',
    // headerTooltip: createLabelHelper('period.date.format.tooltip').get('', undefined, {format:DateTimeHelper.getDateFormat()}),
    headerIconTooltip: createLabelHelper('period.date.format.tooltip').get('', undefined, {
      format: DateTimeHelper.getDateFormat(),
    }),
  };
};

const getColumnsStateFromStorage = function (storageKey) {
  let gridsConfig = JSON.parse(localStorage.getItem('gridsConfig'));
  return gridsConfig ? gridsConfig[storageKey] ?? null : null;
}

const setColumnsStateToStorage = function (storageKey, columnsState) {
  let gridsConfig = JSON.parse(localStorage.getItem('gridsConfig'));
  if (!gridsConfig) {
    gridsConfig = {};
  }
  gridsConfig[storageKey] = columnsState;
  localStorage.setItem('gridsConfig', JSON.stringify(gridsConfig));
}

const removeColumnsStateFromStorage = function (storageKey) {
  let gridsConfig = JSON.parse(localStorage.getItem('gridsConfig'));
  if (gridsConfig && gridsConfig[storageKey]) {
    delete gridsConfig[storageKey];
    localStorage.setItem('gridsConfig', JSON.stringify(gridsConfig));
  }
}

const getFiltersStateFromStorage = function (storageKey) {
  let gridsConfig = JSON.parse(sessionStorage.getItem('gridsConfig'));
  return gridsConfig ? gridsConfig[storageKey] ?? null : null;
}

const setFiltersStateToStorage = function (storageKey, columnsState) {
  let gridsConfig = JSON.parse(sessionStorage.getItem('gridsConfig'));
  if (!gridsConfig) {
    gridsConfig = {};
  }
  gridsConfig[storageKey] = columnsState;
  sessionStorage.setItem('gridsConfig', JSON.stringify(gridsConfig));
}

const removeFiltersStateFromStorage = function (storageKey) {
  let gridsConfig = JSON.parse(sessionStorage.getItem('gridsConfig'));
  if (gridsConfig && gridsConfig[storageKey]) {
    delete gridsConfig[storageKey];
    sessionStorage.setItem('gridsConfig', JSON.stringify(gridsConfig));
  }
}

const headerValueGetter = function (params, value='') {
  switch (params.location) {
    case 'columnToolPanel':
      return value ? value : params.column?.colDef?.headerComponentParams?.headerTooltip || '' ;
    default:
      return '';
  }
}

const isFilterActivated = function(filterName, filtersState=[]) {
  if (!filterName || !filtersState) {
    return false;
  }

  let filter = filtersState.find (item => filterName === item.filterName);

  return filter?.values?.length > 0 ?? false;
}

const getActivatedFilter = function(filterName, filtersState=[]) {
  if (!filterName || !filtersState) {
    return {values: [], extraData: []};
  }

  let filter = filtersState.find (item => filterName === item.filterName);

  return filter?.values?.length > 0 ?
    {values: filter.values, extraData: filter.extraData} : {values: [], extraData: []};
}

//Note: Use this function to ensure proper alignment for the first and last columns' filters.
//e.g.: <FilterLayout filterAlignment={GridHelper.getFilterAlignment(this.props.column, this.props.api?.getColumnState())}
const getFilterAlignment = function(column, columnsState) {
  const index = columnsState?.findIndex(item => item.colId === column?.colId) ?? -1;
  if (index > -1) {
    return (index < 2) ? filterAlignmentTypes.LEFT : (columnsState?.length - index <= 2) ? filterAlignmentTypes.RIGHT : undefined;
  }
}



export default {
  getDateFormatHeaderIcon,
  getDataQueryForExport,
  applyRowSelectionAsFilter,
  createGetDataQuery,
  createGetDataQueryByGridData,
  getFilterValues,
  createColumnMetaData,
  getColumnsStateFromStorage,
  setColumnsStateToStorage,
  removeColumnsStateFromStorage,
  getFiltersStateFromStorage,
  setFiltersStateToStorage,
  removeFiltersStateFromStorage,
  headerValueGetter,
  isFilterActivated,
  getActivatedFilter,
  getFilterAlignment,
};
